
import {
  AUTH_PROVIDER_GOOGLE,
} from '../constants';

import {
  gapiCall,
  REGISTRATION_REQUIRED_MESSAGE,
  CREDENTIALS_REQUIRED_MESSAGE,
  reloadAuthResponse,
  signIn
} from './gapi';

// create user action types
export const CREATE_USER = "CREATE_USER";
export const CREATE_USER_SUCCESS = "CREATE_USER_SUCCESS";
export const CREATE_USER_ERROR = "CREATE_USER_ERROR";

// request user action types
export const REQUEST_USER = "REQUEST_USER";
export const REQUEST_USER_SUCCESS = "REQUEST_USER_SUCCESS";
export const REQUEST_USER_ERROR = "REQUEST_USER_ERROR";

// request account action types
export const REQUEST_ACCOUNT = "REQUEST_ACCOUNT";
export const REQUEST_ACCOUNT_SUCCESS = "REQUEST_ACCOUNT_SUCCESS";
export const REQUEST_ACCOUNT_ERROR = "REQUEST_ACCOUNT_ERROR";

// user alert action types
export const DISMISS_USER_ALERT = "DISMISS_USER_ALERT";
export const DISMISS_USER_ALERT_SUCCESS = "DISMISS_USER_ALERT_SUCCESS";
export const DISMISS_USER_ALERT_ERROR = "DISMISS_USER_ALERT_ERROR";

/**
 * Create user action creator
 * @param {Object} user The user to create
 */
const createUser = user => ({
  type: CREATE_USER,
  user
})

/**
 * Create user success action creator
 * @param {Object} user The created user
 */
const createUserSuccess = user => ({
  type: CREATE_USER_SUCCESS,
  user
})

/**
 * Create user error action creator
 * @param {Object} user The user to create
 * @param {Error} error 
 */
const createUserError = (user, error) => ({
  type: CREATE_USER_ERROR,
  user,
  error
})

/**
 * Request user by ID action creator
 * // FIXME should probably be userId
 * @param {String} id The user ID
 */
const requestUser = id => ({
  type: REQUEST_USER,
  id
})

/**
 * Request user by ID success action creator
 * @param {Object} user The fetched user
 */
const requestUserSuccess = user => ({
  type: REQUEST_USER_SUCCESS,
  user
})

/**
 * Request user by ID error action creator
 * @param {String} userId 
 * @param {Error} error 
 */
const requestUserError = (userId, error) => ({
  type: REQUEST_USER_ERROR,
  userId,
  error
})

/**
 * Request account by user ID action creator
 * @param {String} userId The account owner's ID
 */
const requestAccount = userId => ({
  type: REQUEST_ACCOUNT,
  userId
})

/**
 * Request account by user ID success action creator
 * @param {Object} account The user's account 
 * @param {String} userId The user's ID
 */
const requestAccountSuccess = (account, userId) => ({
  type: REQUEST_ACCOUNT_SUCCESS,
  userId,
  account
})

/**
 * Request account by user ID error action creator
 * @param {Error} error 
 * @param {String} userId 
 */
const requestAccountError = (error, userId) => ({
  type: REQUEST_ACCOUNT_ERROR,
  userId,
  error
})

/**
 * Dismiss user alert by userId and alertId action creator
 * @param {String} userId 
 * @param {Long} alertId 
 */
const dismissUserAlert = (userId, alertId) => ({
  type: DISMISS_USER_ALERT,
  userId,
  alertId
})

/**
 * Dismiss user alert error action creator
 * @param {Error} error 
 */
const dismissUserAlertSuccess = () => ({
  type: DISMISS_USER_ALERT_SUCCESS,
})

/**
 * Dismiss user alert error action creator
 * @param {Error} error 
 */
const dismissUserAlertError = (error) => ({
  type: DISMISS_USER_ALERT_ERROR,
  error
})

/**
 * Dismiss a user alert by userId and alertId
 * @param {String} userId 
 * @param {Long} alertId 
 */
export const doDismissUserAlert =(userId, alertId) => dispatch => 
  dispatch(dismissUserAlert(userId, alertId))
  && dispatch(gapiCall(client => client.billing.users.alerts.dismiss({userId, alertId})))
    .then(() => dispatch(dismissUserAlertSuccess()))
    .catch(error => dispatch(dismissUserAlertError(error)));

/**
 * Fetches a user by ID
 * @param {String} userId 
 */
export const fetchUser = userId => dispatch =>
  dispatch(requestUser(userId))
  && dispatch(gapiCall(client => client.billing.users.get({ userId })))
    .then(user => dispatch(requestUserSuccess(user)))
    .catch(error => dispatch(requestUserError(userId, error)));

/**
 * Creates a user in the API
 * (Only allowed by user for himself, or by superadmin)
 * @param {Object} user 
 */
const doCreateUser = user => dispatch =>
  dispatch(createUser(user))
  && dispatch(gapiCall(client => client.billing.users.create(user)))
    .then(newUser => dispatch(createUserSuccess(newUser)))
    .catch(error => dispatch(createUserError(user, error)));

/**
 * Fetches an account by user ID
 * Retrieves the account's subscriptions, and subscriptions.resources
 * @param {String} userId The owner of the account
 */
export const fetchAccountByUserId = userId => dispatch =>
  dispatch(requestAccount(userId))
  && dispatch(gapiCall(client => client.billing.users.account.get({
    userId,
    includes: "subscriptions,subscriptions.resources"
  })))
    .then(account => dispatch(requestAccountSuccess(account, userId)))
    .catch(error => dispatch(requestAccountError(error, userId)));

/**
 * Creates a user from the google profile
 * @param {Object} googleUser 
 */
const createUserFromGoogleUser = googleUser => dispatch =>
  dispatch(doCreateUser({
    authProvider: AUTH_PROVIDER_GOOGLE,
    authId: googleUser.id,
    firstName: googleUser.firstName,
    lastName: googleUser.lastName,
    email: googleUser.email
  }));


/**
 * Fetches a user by Google ID
 * FIXME: hardcoded naming convention, should use billing.users.getByAuth
 * @param {String} googleId The google ID of the user to fetch
 */
export const fetchUserByGoogleId = googleId => dispatch =>
  dispatch(fetchUser('google_' + googleId));

/**
 * Fetches the user corresponding to the passed google User
 * @param {Object} googleUser The google user (see gapi actions)
 */
export const fetchUserFromGoogleUser = googleUser => dispatch =>
  dispatch(fetchUserByGoogleId(googleUser.id))


/** 
 * Fetches current user, and creates it if needed
 * // FIXME: handle errors
 * // FIXME: tests
*/
export const fetchMe = () => (dispatch, getState) => {
  const { googleUser } = getState();
  if (!googleUser) {
    throw new Error("You must be signed in with Google");
  }

  // identify ZenDesk user
  try {
    (window.zE)(function() {
      (window.zE).identify({
        name: `${googleUser.firstName} ${googleUser.lastName}`,
        email: googleUser.email,
      });
    });
  } catch ( e ) { }

  return dispatch(fetchUserFromGoogleUser(googleUser))
    .then(action => {
      if (action.error) {
        if (action.error.code === 401) {
          switch (action.error.message) {
            case REGISTRATION_REQUIRED_MESSAGE:
              // create the user from google user
              return dispatch(createUserFromGoogleUser(googleUser));
            case CREDENTIALS_REQUIRED_MESSAGE:
              // attempt to refresh token
              return dispatch(reloadAuthResponse())
                // otherwise, attempt to re-sign in
                .catch(err => dispatch(signIn()));
            default:
              return dispatch(signIn());
          }
        }
        if (action.error.code >= 400 && action.error.code < 500) {
          // create users in all other 4xx errors
          return dispatch(createUserFromGoogleUser(googleUser));
        }
      }
    });
}



// WEBPACK FOOTER //
// ./src/actions/users.js